import { useEffect, useRef } from 'react';
import { SFSubscription, SFSubscriptionProduct } from 'pages/api/account/subscriptions';
import { normalizePrice } from './currency';
import {
  Subscription,
  SubscriptionBillingAddress,
  SubscriptionOrderSchedule,
  SubscriptionProduct,
  SubscriptionShippingAddress,
} from '@prisma/client';
import { ProductData, SubscriptionProductData } from '../lib/bigcommerce/types';
import { format } from 'date-fns';
import { AddressTypes } from '../lib/constants';
import { isStoreClosed, storeReopens } from 'lib/shipping/shipping';
import { getNextCadenceDay } from './date';
import { getFrequencyDays } from 'lib/subscription/frequency';

export const extractOrderTotal = (subscription: SFSubscription) => {
  const calculate = (acc: number, product: SFSubscriptionProduct) =>
    acc + Number(product.price) * product.quantity;
  return subscription.items.reduce(calculate, 0) + Number(subscription.shippingCost);
};

export const updateSubscriptionsTotal = (subscriptions: SFSubscription[]) =>
  subscriptions.map((subscription) => ({
    ...subscription,
    total: normalizePrice(extractOrderTotal(subscription).toString()).toString(),
  }));

export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const convertProduct: (
  product: Partial<ProductData & { variantId: number }>
) => Omit<SFSubscriptionProduct, 'id'> = (product) => ({
  productId: product.entityId,
  variantId: product.variantId,
  sku: product.sku,
  name: product?.name || 'Unrecognised product',
  price: product.price,
  total: product.price,
  quantity: 1,
  image: product?.defaultImage,
  minWeight: product?.weightSummary,
});

export const normalizeProduct = (
  product: SubscriptionProduct,
  extendedProduct: SubscriptionProductData,
  products: Array<ProductData>
) => {
  const bcProductDetails = products.filter((each) => each.entityId === product.productId)[0];
  const sfProduct: SFSubscriptionProduct = {
    id: product.id,
    productId: product.productId,
    sku: product.sku,
    name: extendedProduct?.name || 'Unrecognised product',
    price: product.price_inc_tax.toNumber(),
    total: product.price_inc_tax.mul(product.quantity).toNumber(),
    quantity: product.quantity,
    image: bcProductDetails?.defaultImage,
    minWeight: bcProductDetails?.weightSummary,
  };
  return sfProduct;
};

type ExtendedSubscription = Subscription & {
  products: SubscriptionProduct[];
  shipping_address: SubscriptionShippingAddress;
  billing_address: SubscriptionBillingAddress;
  subscription_orders: SubscriptionOrderSchedule[];
};

const nextShippingDate = (sub: ExtendedSubscription) => {
  const date = sub.subscription_orders?.[0]?.delivery_date || sub.delivery_date;
  if (date) {
    if (isStoreClosed()) {
      return getNextCadenceDay(date, storeReopens, getFrequencyDays(sub.frequency));
    }
    return date;
  }

  return null;
};

export const normalizeSubscription: (
  sub: ExtendedSubscription,
  catalogue: SubscriptionProductData[],
  products: Array<ProductData>
) => SFSubscription = (sub, catalogue, products) => ({
  id: sub.id,
  alias: sub.alias,
  active: sub.active,
  currency: 'NZD',
  frequency: sub.frequency,
  total: normalizePrice(sub.total_inc_tax),
  deleted: !!sub.deleted_at,
  label: format(sub.created_at, 'EEEE ') + sub.frequency,
  startDate: sub.created_at,
  shippingCost: normalizePrice(sub.shipping_cost_inc_tax),
  deliveryDate: nextShippingDate(sub),
  shipping_address: sub.shipping_address,
  billing_address: sub.billing_address,
  items: sub.products.map((product) =>
    normalizeProduct(
      product,
      catalogue.find((x) => x.entityId === product.productId),
      products
    )
  ),
});

const isSameAddress = (
  base: SubscriptionShippingAddress | SubscriptionBillingAddress,
  current: SubscriptionShippingAddress | SubscriptionBillingAddress
) =>
  base.city === current.city &&
  base.company === current.company &&
  base.country === current.country &&
  base.country_iso2 === current.country_iso2 &&
  base.email === current.email &&
  base.first_name === current.first_name &&
  base.last_name === current.last_name &&
  base.phone === current.phone &&
  base.state === current.state &&
  base.street_1 === current.street_1 &&
  base.street_2 === current.street_2 &&
  base.zip === current.zip;

export const getAddressesModified = (base, current) => {
  const modified = [];

  modified.push(
    base.id === current.id && !isSameAddress(base.shipping_address, current.shipping_address)
      ? AddressTypes.SHIPPING
      : null
  );
  modified.push(
    base.id === current.id && !isSameAddress(base.billing_address, current.billing_address)
      ? AddressTypes.BILLING
      : null
  );
  return modified.filter((x) => x);
};
